import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { ActionCreatorsMapObject, bindActionCreators } from 'redux';

// eslint-disable-next-line
import once from 'lodash/once';
// eslint-disable-next-line
import isEmpty from 'lodash/isEmpty';

import { NotificationAction } from '@common/typescript/objects/NotificationAction';
import { Notification } from '@common/typescript/objects/Notification';
import { BaseUser } from '@common/react/objects/BaseUser';
import { AfterLogout, useApplicationContext } from '@common/react/components/Core/Application/Application';

import { CompanySettings, PrescriptionDeliveryType } from '@app/objects/Company';
import { ApplicationState } from '@app/store';
import { getActionCreators } from '@app/store/Item';
import { User } from '@app/objects/User';

export interface CompanySettingsContext {
	companySettings: CompanySettings;
	checkCompanyErxAccess: (prescriptionDeliveryType: PrescriptionDeliveryType) => boolean;
	updateCompanySettings: (data: Partial<CompanySettings>) => void;
}

export const createCompanySettingsContext = once(() => React.createContext({} as CompanySettingsContext));

export const useCompanySettingsContext: () => CompanySettingsContext = () => {
	const context: CompanySettingsContext = React.useContext(createCompanySettingsContext());

	if (isEmpty(context)) throw 'Need CompanySettings context!';

	return context;
};

const CompanySettingsSetter: React.FC = ({
	children,
}) => {
	const CompanySettingsContext = createCompanySettingsContext();

	const companySettings = useSelector((state: ApplicationState) => state.companySettings.item, shallowEqual);

	const { subscribeUntilLogout, getUser } = useApplicationContext();

	const dispatch = useDispatch();
	const companySettingsActions: ActionCreatorsMapObject = bindActionCreators(getActionCreators<CompanySettings>(), dispatch);

	const checkCompanyErxAccess = (prescriptionDeliveryType: PrescriptionDeliveryType) =>
		companySettings.erxSettings.some((ce) => ce.prescriptionDeliveryType === prescriptionDeliveryType);

	const updateCompanySettings = (data: Partial<CompanySettings>) => {
		companySettingsActions.updateItem('companySettings', {
			...companySettings,
			...data,
		});
	};

	const user = getUser<User>();

	const handle = (notification: Notification<BaseUser>) => {
		const data = notification.data;
		if (notification.objectType === 'Init') {
			companySettingsActions.initStorage('companySettings', data.companySettings, 'companySettings');
		} else if (notification.objectType === 'company') {
			const inquiryCount = notification.data?.values?.newInquiryCounter;
			const reviewCount = notification.data?.values?.newReviewCounter;
			const companyId = notification.data?.id;

			if (typeof (inquiryCount) !== 'undefined' && companyId === user?.companyId) {
				if (notification.action === NotificationAction.Add) {
					updateCompanySettings({
						newInquiryCount: inquiryCount,
					});
				} else {
					updateCompanySettings({
						newInquiryCount: companySettings.newInquiryCount + inquiryCount,
					});
				}
			}
			if (typeof (reviewCount) !== 'undefined' && companyId === user?.companyId) {
				if (notification.action === NotificationAction.Update) {
					updateCompanySettings({
						newReviewCount: companySettings.newReviewCount + reviewCount,
					});
				}
			}
		}
	};

	subscribeUntilLogout(handle);

	return (
		<>
			<AfterLogout callback={() => companySettingsActions.initStorage('companySettings', {}, 'companySettings')} />
			<CompanySettingsContext.Provider value={{ companySettings, checkCompanyErxAccess, updateCompanySettings }}>
				{children}
			</CompanySettingsContext.Provider>
		</>
	);
};

export default CompanySettingsSetter;
